import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, FileSpreadsheet, FileText, Layers, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, downloadFile, fmtDay, fmtLakhValue, fmtPct } from "@/lib/api";
import { FamilyTrendChart } from "@/components/FamilyTrendChart";

const band = (v) =>
  v === null || v === undefined
    ? "hsl(var(--muted-foreground))"
    : v >= 95
      ? "hsl(var(--healthy))"
      : v >= 80
        ? "hsl(var(--watch))"
        : "hsl(var(--at-risk))";

const Bar = ({ family, row }) => {
  const pct = row.cumulative_pct;
  const colour = band(pct);
  return (
    <div
      className="grid grid-cols-[92px_1fr] items-center gap-3 py-2 sm:grid-cols-[92px_1fr_190px]"
      data-testid={`family-row-${family.key}-${row.plant}`}
    >
      <Link
        to={`/compliance-data?plant=${row.plant}&q=${row.code}`}
        className="min-w-0"
        title={`${row.code} — ${row.title}`}
      >
        <span className="block font-display text-[12px] font-black tracking-tight hover:text-primary">
          {row.plant}
        </span>
        <span className="font-data block truncate text-[9px] text-muted-foreground">
          {row.code}
        </span>
      </Link>

      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate text-[11px] text-muted-foreground">{row.title}</span>
          <span
            className="font-data shrink-0 text-[13px] font-bold tabular-nums"
            style={{ color: colour }}
            data-testid={`family-pct-${family.key}-${row.plant}`}
          >
            {pct === null || pct === undefined ? "no reading yet" : fmtPct(pct)}
          </span>
        </div>
        <div className="mt-1 h-2.5 w-full bg-muted" data-testid={`family-bar-${family.key}-${row.plant}`}>
          <div
            className="h-full transition-[width] duration-700 ease-out"
            style={{
              width: `${Math.max(pct ? 1.5 : 0, Math.min(100, pct || 0))}%`,
              backgroundColor: colour,
            }}
          />
        </div>
      </div>

      <div className="font-data col-span-2 flex flex-wrap items-baseline gap-x-4 gap-y-0.5 pl-[104px] text-[10px] text-muted-foreground sm:col-span-1 sm:justify-end sm:pl-0 sm:text-right">
        <span>
          KPI{" "}
          <b className="text-foreground">
            {row.kpi_actual ?? "—"}
            {row.kpi_target !== null && row.kpi_target !== undefined
              ? ` / ${row.kpi_target}`
              : ""}
          </b>
          {row.kpi_unit ? ` ${row.kpi_unit}` : ""}
        </span>
        <span>
          Impact{" "}
          <b style={{ color: row.impact_lakh ? "hsl(var(--healthy))" : undefined }}>
            {row.impact_lakh ? fmtLakhValue(row.impact_lakh) : "—"}
          </b>
        </span>
        <span>
          {row.days} day(s) · {row.last_updated ? fmtDay(row.last_updated) : "never filled"}
        </span>
      </div>
    </div>
  );
};

/**
 * The same work at several units, side by side.
 *
 * The grouping is the portal's own reading of the KPI and the title (`families.py`), unless SDTD
 * has written a family on the usecase — then that wins. Only work being run at more than one unit
 * is drawn here; that is the whole question this block answers.
 */
export const ComplianceFamilies = ({ plant = "", q = "" }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api
      .get("/compliance/families", { params: { plant, q } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [plant, q]);

  useEffect(() => {
    const t = setTimeout(load, 150);
    return () => clearTimeout(t);
  }, [load]);

  const take = async (format, key, label) => {
    setBusy(`${key}-${format}`);
    try {
      await downloadFile(
        "/compliance/families/report",
        { format, key, plant, q },
        `${label} family compliance.${format}`,
      );
      toast.success(`${label} — taken out as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const Take = ({ familyKey, label, testPrefix }) => (
    <span className="flex items-center gap-1.5">
      <button
        type="button"
        disabled={!!busy}
        onClick={() => take("xlsx", familyKey, label)}
        data-testid={`${testPrefix}-xlsx`}
        title="The comparison and every day on record, as a workbook"
        className="flex items-center gap-1.5 border border-border px-2 py-1 font-display text-[9px] font-bold uppercase tracking-widest transition-colors duration-200 hover:border-primary hover:text-primary disabled:opacity-50"
      >
        {busy === `${familyKey}-xlsx` ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <FileSpreadsheet className="h-3 w-3" />
        )}
        Excel
      </button>
      <button
        type="button"
        disabled={!!busy}
        onClick={() => take("pdf", familyKey, label)}
        data-testid={`${testPrefix}-pdf`}
        title="A page fit to carry into a review"
        className="flex items-center gap-1.5 border border-border px-2 py-1 font-display text-[9px] font-bold uppercase tracking-widest transition-colors duration-200 hover:border-primary hover:text-primary disabled:opacity-50"
      >
        {busy === `${familyKey}-pdf` ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <FileText className="h-3 w-3" />
        )}
        PDF
      </button>
    </span>
  );

  if (!data)
    return (
      <div className="flex h-24 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  if (!data.families.length)
    return (
      <div data-testid="families-empty">
        <Layers className="h-5 w-5 text-muted-foreground" />
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          No two units are keeping compliance on the same work yet. Of the {data.totals.tracked}{" "}
          usecase(s) under watch here, each one stands on its own. The moment a second unit starts
          filling the same KPI — ferro alloy, slag rate, freight cost — they will appear here on
          one scale.
        </p>
      </div>
    );

  return (
    <div className="space-y-6" data-testid="compliance-families">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p
          className="max-w-3xl text-[11px] leading-relaxed text-muted-foreground"
          data-testid="families-summary"
        >
          <b className="font-data text-foreground">{data.totals.families}</b> piece(s) of work are
          being run at more than one unit — {data.totals.in_families} usecase(s) in all. The portal
          reads this from the KPI and the title; write a <b>Family</b> on a usecase to group it
          yourself. {data.totals.alone} usecase(s) have no peer to be compared with.
        </p>
        <Take familyKey="" label="every family" testPrefix="families-all" />
      </div>

      {data.families.map((family) => (
        <div key={family.key} className="border border-border bg-card" data-testid={`family-${family.key}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-border bg-muted/40 px-4 py-3">
            <div className="min-w-0">
              <h3 className="font-display text-base font-black tracking-tight">
                {family.label}
                {family.manual && (
                  <span className="font-data ml-2 border border-border px-1.5 py-0.5 text-[8.5px] uppercase tracking-widest text-muted-foreground">
                    named by SDTD
                  </span>
                )}
              </h3>
              <p className="font-data mt-0.5 text-[10px] text-muted-foreground">
                {family.units.join(" · ")} · {family.kpi_name || "KPI not named"}
                {family.kpi_unit ? ` · ${family.kpi_unit}` : ""}
              </p>
            </div>
            <div className="font-data flex flex-wrap items-baseline gap-x-5 text-[10px] text-muted-foreground">
              <span>
                Average{" "}
                <b className="text-[12px]" style={{ color: band(family.avg_pct) }}>
                  {fmtPct(family.avg_pct)}
                </b>
              </span>
              {family.spread_pct !== null && (
                <span data-testid={`family-spread-${family.key}`}>
                  Spread <b className="text-foreground">{family.spread_pct} pts</b> ·{" "}
                  {family.leader} leads, {family.laggard} trails
                </span>
              )}
              <span>
                Impact{" "}
                <b style={{ color: family.impact_lakh ? "hsl(var(--healthy))" : undefined }}>
                  {family.impact_lakh ? fmtLakhValue(family.impact_lakh) : "—"}
                </b>
                {family.target_impact_lakh
                  ? ` of ${fmtLakhValue(family.target_impact_lakh)}`
                  : ""}
              </span>
            </div>
          </div>
          <div className="divide-y divide-border px-4 py-1">
            {family.rows.map((row) => (
              <Bar key={row.project_id} family={family} row={row} />
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-2">
            <button
              type="button"
              onClick={() => setOpen((o) => ({ ...o, [family.key]: !o[family.key] }))}
              data-testid={`family-trend-toggle-${family.key}`}
              disabled={!(family.series || []).length}
              className="flex items-center gap-1.5 border border-border px-2 py-1 font-display text-[9px] font-bold uppercase tracking-widest transition-colors duration-200 hover:border-primary hover:text-primary disabled:opacity-50"
            >
              <Activity className="h-3 w-3" />
              {open[family.key] ? "Hide the trend" : "Trend, unit against unit"}
            </button>
            <Take
              familyKey={family.key}
              label={family.label}
              testPrefix={`family-export-${family.key}`}
            />
          </div>
          {open[family.key] && !!(family.series || []).length && (
            <div className="border-t border-border px-4 py-3">
              <p className="mb-2 text-[10.5px] text-muted-foreground">
                Cumulative compliance %, by calendar date — a unit that began later simply starts
                later on the chart, and a line holds steady on a day nothing was filled.
              </p>
              <FamilyTrendChart
                series={family.series}
                lines={family.lines}
                testId={`family-trend-${family.key}`}
              />
            </div>
          )}
          {!!family.waiting.length && (
            <p
              className="border-t border-border px-4 py-2 text-[10.5px] text-muted-foreground"
              data-testid={`family-waiting-${family.key}`}
            >
              {family.waiting.join(", ")} {family.waiting.length > 1 ? "have" : "has"} filled no
              compliance per cent yet, so {family.waiting.length > 1 ? "they stand" : "it stands"}{" "}
              at the foot rather than at nought.
            </p>
          )}
        </div>
      ))}
    </div>
  );
};
